import React, { useEffect, useState } from "react";
import "./QuestionModal.css";
import { DeleteIcon } from "../../../../icons/reactIcons";
import { QuestionCategory } from "../../../../apis/questionApis";

/* ---------- Types ---------- */

interface OptionForm {
  id?: string; // existing option id (edit mode)
  optionName: string;
  isActive: boolean; // soft delete flag
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  submitting?: boolean;
  categories: QuestionCategory[];
  initialData?: any; // undefined → create mode
}

/* ---------- Component ---------- */

const EditQuestionModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  submitting,
  categories,
  initialData,
}) => {
  const isCreate = !initialData;

  const [questionName, setQuestionName] = useState("");
  const [questionCategoryId, setQuestionCategoryId] = useState("");
  const [options, setOptions] = useState<OptionForm[]>([
    { optionName: "", isActive: true },
    { optionName: "", isActive: true },
  ]);
  const [errors, setErrors] = useState({
    questionName: false,
    questionCategoryId: false,
    options: [] as boolean[],
  });

  /* ---------- Populate Edit Data ---------- */

  useEffect(() => {
    if (initialData) {
      setQuestionName(initialData.questionName);
      setQuestionCategoryId(initialData.questionCategoryId);

      setOptions(
        initialData.options.map((o: any) => ({
          id: o.id,
          optionName: o.optionName,
          isActive: o.isActive ?? true,
        })),
      );
    } else {
      setQuestionName("");
      setQuestionCategoryId("");
      setOptions([{ optionName: "", isActive: true }]);
    }
  }, [initialData, open]);

  if (!open) return null;

  /* ---------- Option Handlers ---------- */

  const addOption = () => {
    const lastOption = options[options.length - 1];

    if (!lastOption.optionName.trim()) {
      setErrors((prev) => ({
        ...prev,
        options: options.map((o, i) =>
          i === options.length - 1 ? true : prev.options[i],
        ),
      }));
      return;
    }

    setOptions((prev) => [...prev, { optionName: "", isActive: true }]);
  };

  const updateOption = (index: number, value: string) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, optionName: value } : opt)),
    );

    setErrors((prev) => ({
      ...prev,
      options: prev.options.map((err, i) => (i === index ? false : err)),
    }));
  };

  // SOFT DELETE (do NOT remove from array)
  const removeOption = (index: number) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, isActive: false } : opt)),
    );
  };

  /* ---------- Submit ---------- */

  const handleSubmit = () => {
    let hasError = false;

    const optionErrors = options.map((o) => {
      if (!o.optionName.trim() && o.isActive) {
        hasError = true;
        return true;
      }
      return false;
    });

    const newErrors = {
      questionName: !questionName.trim(),
      questionCategoryId: !questionCategoryId,
      options: optionErrors,
    };

    if (
      newErrors.questionName ||
      newErrors.questionCategoryId ||
      optionErrors.includes(true)
    ) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      questionName,
      questionDescription: "",
      questionCategoryId,
      options: options.map((o, i) => ({
        id: o.id,
        optionName: o.optionName,
        optionOrder: i + 1,
        isActive: o.isActive,
      })),
    });
  };

  /* ---------- UI ---------- */

  return (
    <div className="questionModal-overlay">
      <div className="questionModal-container large">
        <h3>{isCreate ? "Create Question" : "Edit Question"}</h3>

        <div className="create-questionModal-body">
          {/* Question */}
          <label>
            Question <span className="required">*</span>
          </label>
          <textarea
            className={`input ${errors.questionName ? "input-error" : ""}`}
            value={questionName}
            required
            onChange={(e) => {
              setQuestionName(e.target.value);
              setErrors((prev) => ({ ...prev, questionName: false }));
            }}
          />

          {/* Category */}
          <label>
            Category <span className="required">*</span>
          </label>
          <select
            className={`input ${errors.questionCategoryId ? "input-error" : ""}`}
            value={questionCategoryId}
            required
            onChange={(e) => {
              setQuestionCategoryId(e.target.value);
              setErrors((prev) => ({ ...prev, questionCategoryId: false }));
            }}
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* Options */}
          {options
            .filter((opt) => opt.isActive) // hide soft-deleted options
            .map((opt, i) => (
              <div key={opt.id ?? i} className="answer-row">
                <input
                  className={`input ${errors.options[i] ? "input-error" : ""}`}
                  placeholder={`Option ${i + 1}`}
                  value={opt.optionName}
                  required
                  onChange={(e) => updateOption(i, e.target.value)}
                />

                <button
                  className="icon-btn delete"
                  onClick={() => removeOption(i)}
                  disabled={i === 0 && options.length === 1}
                >
                  <DeleteIcon />
                </button>
              </div>
            ))}

          <button className="add-more" onClick={addOption}>
            + Add more option
          </button>
        </div>

        {/* Footer */}
        <div className="questionModal-footer">
          <button onClick={onClose} className="btn cancel">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn update"
            disabled={submitting}
          >
            {isCreate ? "Create" : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditQuestionModal;
